import { useMemo } from 'react';
import type { Api } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { Field, ReadOnlyNote, SaveBar, Section, useDraft } from '../ui';
import { useSpecSave, type EditableSpec } from './AgentSettings';

type Intent = { id: string; description: string; action: string; target?: string };
type Hours = { days: number[]; start: string; end: string };

const FALLBACK_ZONES = ['Europe/Istanbul', 'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Moscow', 'Asia/Dubai', 'Asia/Riyadh', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'UTC'];
export const zones = (): string[] => {
  try {
    return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf('timeZone');
  } catch {
    return FALLBACK_ZONES;
  }
};
export const browserZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Actions tab: whether the agent may promise a callback (and when), the business time zone and working
 * hours. Callbacks are agreed and placed only inside the hours (ADR-027).
 */
export const ActionsPanel = ({ api, t, agentId, spec, onSaved }: { api: Api; t: T; agentId: string; spec: EditableSpec; onSaved: () => void }) => {
  // ADR-071: without the agents permission the settings are shown read-only.
  const canEdit = useCan()(PERM.agents);
  const intents = (spec.globalIntents as Intent[] | undefined) ?? [];
  const cb = intents.find((g) => g.action === 'schedule_callback');
  const d = useDraft({
    callback: Boolean(cb),
    description: cb?.description ?? t('actions.callback.defaultDescription'),
    timezone: (spec.timezone as string | undefined) ?? '',
    limited: Boolean(spec.businessHours),
    hours: (spec.businessHours as Hours | undefined) ?? { days: [1, 2, 3, 4, 5], start: '09:00', end: '18:00' },
  });
  const s = useSpecSave(api, agentId, onSaved, t);
  const v = d.value;
  const all = useMemo(zones, []);
  const toggleDay = (day: number) => d.set({ ...v, hours: { ...v.hours, days: v.hours.days.includes(day) ? v.hours.days.filter((x) => x !== day) : [...v.hours.days, day].sort() } });

  const payload = () => {
    const rest = intents.filter((g) => g.action !== 'schedule_callback');
    const callbackIntent = v.callback ? [{ id: cb?.id ?? 'callback', action: 'schedule_callback', description: v.description.trim() || t('actions.callback.defaultDescription') }] : [];
    return {
      globalIntents: [...rest, ...callbackIntent],
      timezone: v.timezone || null,
      businessHours: v.limited && v.timezone ? v.hours : null,
    };
  };
  const invalid = v.limited && (v.hours.days.length === 0 || v.hours.start >= v.hours.end);

  return (
    <div className="settings">
      <fieldset disabled={!canEdit} className="readonly-fieldset">
        <ReadOnlyNote t={t} need={PERM.agents} />
        <Section title={t('actions.callback')} description={t('actions.callback.hint')}>
          <div className="choice-row">
            {([true, false] as const).map((on) => (
              <label key={String(on)} className={`choice ${v.callback === on ? 'on' : ''}`}>
                <input type="radio" name="callback" checked={v.callback === on} onChange={() => d.set({ ...v, callback: on })} />
                <span>{t(on ? 'actions.callback.on' : 'actions.callback.off')}<small>{t(on ? 'actions.callback.on.hint' : 'actions.callback.off.hint')}</small></span>
              </label>
            ))}
          </div>
          {v.callback && (
            <Field label={t('actions.callback.when')} hint={t('actions.callback.when.hint')}>
              <textarea rows={2} value={v.description} onChange={(e) => d.set({ ...v, description: e.target.value })} />
            </Field>
          )}
        </Section>
        <Section title={t('actions.timezone')} description={t('actions.timezone.hint')}>
          <div className="row">
            <select value={v.timezone} onChange={(e) => d.set({ ...v, timezone: e.target.value })} aria-label={t('actions.timezone')} style={{ maxWidth: 320 }}>
              <option value="">{t('actions.timezone.none')}</option>
              {all.map((z) => <option key={z} value={z}>{z.replaceAll('_', ' ')}</option>)}
            </select>
            {canEdit && v.timezone !== browserZone() && <button type="button" className="ghost" onClick={() => d.set({ ...v, timezone: browserZone() })}>{t('actions.timezone.browser', { zone: browserZone() })}</button>}
          </div>
          {!v.timezone && v.callback && <p className="banner warn small">{t('actions.timezone.needed')}</p>}
        </Section>
        <Section title={t('actions.hours')} description={t('actions.hours.hint')}>
          <div className="choice-row">
            {([false, true] as const).map((limited) => (
              <label key={String(limited)} className={`choice ${v.limited === limited ? 'on' : ''}`}>
                <input type="radio" name="hours" checked={v.limited === limited} disabled={limited && !v.timezone} onChange={() => d.set({ ...v, limited })} />
                <span>{t(limited ? 'actions.hours.limited' : 'actions.hours.any')}<small>{t(limited ? (v.timezone ? 'actions.hours.limited.ready' : 'actions.hours.limited.hint') : 'actions.hours.any.hint')}</small></span>
              </label>
            ))}
          </div>
          {v.limited && (
            <>
              <div className="row">
                {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                  <button key={day} type="button" className={`chip ${v.hours.days.includes(day) ? 'active' : ''}`} onClick={() => toggleDay(day)}>{t(`day.${day}`)}</button>
                ))}
              </div>
              <div className="row">
                <Field label={t('actions.hours.start')}><input type="time" value={v.hours.start} onChange={(e) => d.set({ ...v, hours: { ...v.hours, start: e.target.value } })} /></Field>
                <Field label={t('actions.hours.end')}><input type="time" value={v.hours.end} onChange={(e) => d.set({ ...v, hours: { ...v.hours, end: e.target.value } })} /></Field>
              </div>
              {invalid && <p className="banner error small">{t('actions.hours.invalid')}</p>}
            </>
          )}
        </Section>
      </fieldset>
      <SaveBar t={t} need={PERM.agents} dirty={d.dirty} saving={s.saving} error={s.error} onReset={d.reset} onSave={() => !invalid && void s.save(payload(), d.commit)} />
    </div>
  );
};
